import { html, LitElement, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import '@/components/icon'
import glass from '@/assets/glass.css?inline'
import { lucideMinus, lucidePlus } from '@/icons'
import { defineFormAssociation, FormAssociationController } from '@/shared/form-association'
import { normalizeNumber } from '@/shared/normalize'

import style from './style.css?inline'

@customElement('web-ui-input-number')
export class WebUiInputNumber extends LitElement {
  static formAssociated = true
  static override styles = [unsafeCSS(glass), unsafeCSS(style)]

  @property({ type: String, reflect: true }) placeholder = ''
  @property({ type: String, reflect: true }) name = ''
  @property({ type: Boolean, reflect: true }) disabled = false
  @property({ type: Boolean, reflect: true }) readonly = false
  @property({ type: Boolean, reflect: true }) required = false
  @property({ type: Number, reflect: true })
  get precision(): number {
    return this._precision
  }
  // 精度变化时重算现有值，避免浮点累积误差
  set precision(v: number) {
    const old = this._precision
    this._precision = v
    const current = this.clamp(this.round(this._value))
    if (current !== this._value) {
      this._value = current
      this._formAssociation.sync()
    }
    this.requestUpdate('precision', old)
  }

  @state() private _value = 0
  @state() private _min = 0
  @state() private _max = Infinity
  @state() private _step = 1
  @state() private _focused = false
  private _precision = 0

  @property({ type: Number, reflect: true })
  get value(): number {
    return this._value
  }

  set value(v: number) {
    const clamped = this.clamp(this.round(v))
    const old = this._value
    this._value = clamped
    this._formAssociation.sync()
    this.requestUpdate('value', old)
  }

  private get _isDisabled(): boolean {
    return this.disabled || this._formAssociation.isFormDisabled()
  }

  @property({ type: Number, reflect: true })
  get min(): number {
    return this._min
  }

  set min(v: unknown) {
    this._min = normalizeNumber(v, -Infinity, Infinity, 0)
  }

  @property({ type: Number, reflect: true })
  get max(): number {
    return this._max
  }

  set max(v: unknown) {
    this._max = normalizeNumber(v, -Infinity, Infinity, Infinity)
  }

  @property({ type: Number, reflect: true })
  get step(): number {
    return this._step
  }

  // 步进值，默认 1；若声明了 precision 则以 precision 退化步进
  set step(v: unknown) {
    this._step = normalizeNumber(v, 0, Infinity, 1)
  }

  private get _effectiveStep(): number {
    return this._step
  }

  private get atMin(): boolean {
    return this._value <= this._min
  }

  private get atMax(): boolean {
    return this._value >= this._max
  }

  private readonly _formAssociation = defineFormAssociation<number>({
    host: this,
    getState: () => this._value,
    setState: value => {
      this.value = value
    },
    getFormValue: () => String(this._value),
    getFormState: () => String(this._value),
    restoreState: state => {
      if (typeof state !== 'string') return
      const value = Number(state)
      if (Number.isFinite(value)) this.value = value
    },
    syncValidity: () => this._syncValidity()
  }).make()

  private readonly _formAssociationController = new FormAssociationController(this, this._formAssociation)

  formResetCallback() {
    this._formAssociation.reset()
  }

  formDisabledCallback(disabled: boolean) {
    this._formAssociation.setDisabled(disabled)
  }

  formStateRestoreCallback(state: string | File | FormData | null) {
    this._formAssociation.restore(state)
  }

  override updated() {
    this._syncValidity()
    this.toggleAttribute('focused', this._focused)
  }

  private _syncValidity() {
    const input = this.shadowRoot?.querySelector('input')
    const internals = this._formAssociation.getInternals()
    if (!internals || !input || typeof internals.setValidity !== 'function') return
    if (this._isDisabled || input.validity.valid) {
      internals.setValidity({})
      return
    }
    const flags: ValidityStateFlags = {}
    if (input.validity.valueMissing) flags.valueMissing = true
    if (input.validity.rangeUnderflow) flags.rangeUnderflow = true
    if (input.validity.rangeOverflow) flags.rangeOverflow = true
    if (input.validity.stepMismatch) flags.stepMismatch = true
    internals.setValidity(flags, input.validationMessage, input)
  }

  private clamp(v: number): number {
    return Math.min(this._max, Math.max(this._min, v))
  }

  private round(v: number): number {
    const f = 10 ** this.precision
    return Math.round(v * f) / f
  }

  private setValueAndNotify(v: number) {
    this.value = this.clamp(this.round(v))
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
  }

  private handleStep(direction: 1 | -1) {
    if (this._isDisabled || this.readonly) return
    this.setValueAndNotify(this._value + direction * this._effectiveStep)
  }

  private handleInput(e: Event) {
    if (this.readonly) return
    if (!(e.target instanceof HTMLInputElement)) return
    const raw = e.target.value
    if (raw === '' || raw === '-') return
    this._value = this.clamp(this.round(Number(raw)))
    this._formAssociation.sync()
    this._syncValidity()
  }

  // 原生 change 不 composed，被 shadow root 挡住；文本失焦提交时补发 composed 事件，
  // 与 ArrowUp/ArrowDown 的 change 行为对齐。
  private handleNativeChange(e: Event) {
    if (this.readonly) return
    if (!(e.target instanceof HTMLInputElement)) return
    const raw = e.target.value
    if (raw === '' || raw === '-') return
    this._value = this.clamp(this.round(Number(raw)))
    this._formAssociation.sync()
    this._syncValidity()
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }))
  }

  private handleKeydown(e: KeyboardEvent) {
    if (this._isDisabled || this.readonly) return
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.setValueAndNotify(this._value + this._effectiveStep)
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }))
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.setValueAndNotify(this._value - this._effectiveStep)
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }))
    }
  }

  private handleFocus() {
    if (!this._isDisabled) this._focused = true
  }

  private handleBlur() {
    this._focused = false
  }

  override render() {
    return html`
      <div class="wui-glass wui-input-inner">
        <button
          class="num-btn"
          aria-label="Decrease"
          ?disabled=${this._isDisabled || this.readonly || this.atMin}
          @click=${() => this.handleStep(-1)}
        >
          <web-ui-icon .icon=${lucideMinus}></web-ui-icon>
        </button>
        <input
          type="number"
          placeholder=${this.placeholder}
          name=${this.name}
          .value=${String(this._value)}
          ?disabled=${this._isDisabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          @input=${this.handleInput}
          @change=${this.handleNativeChange}
          @keydown=${this.handleKeydown}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
        />
        <button
          class="num-btn"
          aria-label="Increase"
          ?disabled=${this._isDisabled || this.readonly || this.atMax}
          @click=${() => this.handleStep(1)}
        >
          <web-ui-icon .icon=${lucidePlus}></web-ui-icon>
        </button>
      </div>
    `
  }

  declare readonly $events: {
    input: Event
    change: Event
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'web-ui-input-number': WebUiInputNumber
  }
}
